/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import config from "../config/userListsDelegate.config.json";
import axios from "axios";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { VApplication } from "../../../application";
import moment from "moment-timezone";
import { pretty } from "@vlinder-web/utils-react";
import { GET_USER_LIST_DELEGATE } from "../graphql/getUserListDelegate.query";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

interface userListProp {
  criteria?: any;
  limit?: any;
  skip?: any;
}

export interface IUserListsDelegateMethods extends IPluginMethodMap {
  getUserListsDelegateProps(args: any): Promise<any>;
}

export class UserListsDelegateService implements IAgentPlugin {
  readonly methods: IUserListsDelegateMethods;

  constructor() {
    this.methods = {
      getUserListsDelegateProps: this.getUserListsDelegateProps.bind(this),
    };
  }

  async getUserListsDelegateProps(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    console.log("data==============", pretty(args));
    let filter = {};
    if (args?.criteria?.fullName) {
      filter = {
        ...filter,
        User___firstName: {
          contains: args?.criteria?.fullName,
        },
        // User___lastName: {
        //   contains: args?.criteria?.fullName,
        // },
      };
    }

    if (args?.criteria?.email) {
      filter = {
        ...filter,
        User___email: {
          contains: args?.criteria?.email,
        },
      };
    }

    try {
      if (client) {
        const res = await client.query({
          query: GET_USER_LIST_DELEGATE,
          variables: {
            filter: {
              blockedDelegate: {
                exists: true,
                ne: null,
              },
              ...filter,
            },
            options: {
              limit: args?.size ?? 10,
              skip: args?.offset ?? 0,
              sort: {
                field: "updatedAt",
                order: "desc",
              },
            },
          },
          fetchPolicy: "network-only",
        });
        if (res?.data) {
          let _totalPages = res?.data?.getBoxes?.count
            ? Math.ceil(parseInt(res?.data?.getBoxes?.count) / 10)
            : 1;
          let _currentPage =
            res?.data?.getBoxes?.skip / res?.data?.getBoxes?.limit + 1;
          let _rowData = this.formatBoxTable(res?.data?.getBoxes?.content);
          console.log("_rowData==>", _rowData);

          return {
            ...config,
            ultUsers: _rowData,
            isEmpty: res?.data?.getBoxes?.content?.length <= 0,
            currentPage: _currentPage,
            activePage: _currentPage,
            pageSize: _rowData.length,
            totalCount: res?.data?.getBoxes?.count,
            totalPages: _totalPages,
          };
        }
      }
    } catch (err: any) {
      console.log(`error in table data`, err);
      return {
        isEmpty: true,
      };
    }
  }

  private formatBoxTable(data: any) {
    const _rowData =
      Array.isArray(data) &&
      data.map((item: any) => {
        const user = item?.user || {};

        return {
          id: item?._id || "",

          // User Info
          vuid: user?.vuid || "-",
          name: `${user?.firstName || ""} ${user?.lastName || ""}`.trim(),
          email: user?.email || "-",
          status: user?.accountStatus
            ? this.getFormattedStatus(user?.accountStatus, user?.suspended)
            : "-",
          gender: user?.gender || "-",
          inviteStatus: user?.inviteStatus || "-",

          // Role Info
          role: this._getRole?.(user?.role) || user?.role?.name || "-",

          // Box Info
          delegateName: item?.delegateName || "-",
          costumeID: item?.costumeID || "-",
          boxStatus: item?.boxStatus || "-",
          blockedDelegate: item?.blockedDelegate || "-", // show as true/false
        };
      });
    console.log("rowdata=========", _rowData);
    return _rowData;
  }

  private _getRole(roles: any[]) {
    let _res: any[] = [];
    roles &&
      Array.isArray(roles) &&
      roles.length > 0 &&
      roles.map((item: any, index: number) => {
        _res.push(item?.name);
      });
    return _res;
  }

  private getFormattedStatus(status: string, suspended: boolean) {
    if (suspended) {
      return "blacklisted";
    } else {
      switch (status) {
        case "USER_ADDED":
          return "created";
        case "USER_INVITED":
          return "invited";
        case "USER_VERIFIED":
          return "verified";
        case "USER_ACC_CREATED":
          return "active";
        case "SUSPENDED":
          return "Blacklisted";
        default:
          return "-";
      }
    }
  }

  private getFilter(criteria: any) {
    if (criteria?.fullName) {
      return {
        column: "fullName",
        op: "Contains",
        value: criteria?.fullName.toLowerCase(),
      };
    }
    if (criteria?.email) {
      return {
        column: "email",
        op: "Contains",
        value: criteria?.email.toLowerCase(),
      };
    }
    return null;
  }

  private determineSearchType(criteria: any) {
    if (Object.keys(criteria).length === 0) {
      return null;
    } else if (Object.keys(criteria)[0] === "0") {
      return "multi";
    } else {
      return "normal";
    }
  }

  private getNormalFilter(criteria: any) {
    const resultArray = [];
    if (
      criteria?.accountStatus__in &&
      criteria?.accountStatus__in !== null &&
      criteria?.accountStatus__in !== "undefined"
    ) {
      if (criteria?.accountStatus__in === "SUSPENDED") {
        resultArray.push({
          column: "suspended",
          op: "Equal",
          value: true,
        });
      } else {
        if (criteria?.accountStatus__in === "USER_ACC_CREATED") {
          resultArray.push({
            column: "accountStatus",
            op: "Equal",
            value: "USER_ACC_CREATED",
          });
        } else {
          resultArray.push({
            column: "accountStatus",
            op: "Equal",
            value: criteria?.accountStatus__in,
          });
        }
      }
    }
    return resultArray;
  }
  private getMultiFilter(criteria: any) {
    const options = [
      {
        label: "Masquerader",
        value: "fe93dab0-424b-4aa3-9efa-c001b4c04444",
      },
      {
        label: "Admin",
        value: "fe93dab0-424b-4aa3-9efa-c001b4c03013",
      },
      {
        label: "committeeMember",
        value: "99403c8b-abc1-4796-b61f-78e0c4022792",
      },
      {
        label: "cashier",
        value: "f5bebfee-dc0a-460a-9a3f-c6cde8fed944",
      },
      {
        label: "supervisor",
        value: "ca9521c7-d584-43b8-8c90-79aa3e11bddc",
      },
      {
        label: "cashSupervisor",
        value: "5dbff659-66e4-4a66-b03c-f9b2d3dd6de6",
      },
    ];

    const transformedCriteria: any = [];
    Object.values(criteria).forEach((item: any) => {
      const column = item.filters.inputData.id;
      const op = item.filters.inputData.label;

      // Determine the value based on the available filter keys
      let value = "";
      if (column === "role" && "Equal" in item.filters) {
        const label = item.filters.Equal;
        const option = options.find((opt) => opt.label === label);
        if (option) {
          value = option.value.toLowerCase();
        }
      } else if ("Contains" in item.filters) {
        value = item.filters.Contains.toLowerCase();
      } else if ("Equal" in item.filters) {
        value = item.filters.Equal.toLowerCase();
      } // Add more conditions for other possible filter keys

      transformedCriteria.push({
        column,
        value,
        op,
      });
    });

    return transformedCriteria;
  }
}
